import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, Check, AlertTriangle, DollarSign, Calendar,
  Users, Music, Award, Shield, Pen, Download, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';
import { EditableList } from '@/components/edit/EditableList';

const API = process.env.REACT_APP_BACKEND_URL;

const MembershipAgreement = () => (
  <PageEditProvider slug="membership-agreement">
    <MembershipAgreementInner />
    <PageEditToolbar />
  </PageEditProvider>
);

const MembershipAgreementInner = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    emergencyContact: '',
    emergencyPhone: '',
    membershipType: 'general',
    role: '',
    agreedToRules: false,
    agreedToFines: false,
    agreedToConduct: false,
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0]
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const fines = [
    { violation: 'Late to rehearsal (with prior notification)', amount: 5 },
    { violation: 'Missed rehearsal (without prior notification)', amount: 10 },
    { violation: 'Missed rehearsal before a performance', amount: 15 },
    { violation: 'Late to a performance', amount: 15 },
    { violation: 'Missed performance', amount: 25 },
    { violation: 'Misconduct at rehearsal or performance', amount: 5 },
    { violation: 'Costume not properly fitted or polished', amount: 5 },
    { violation: 'Lack of stage presence or cohesiveness', amount: 10 },
    { violation: 'Unauthorized teaching of choreography', amount: 100 },
    { violation: 'Unauthorized teaching of drum arrangements', amount: 100 },
    { violation: 'Unauthorized use of Escola de Samba Casa Samba property', amount: 50 },
    { violation: 'Wearing Escola de Samba Casa Samba costume for another group', amount: 75 },
  ];

  const suspensionOffenses = [
    'Excessive late or missed rehearsals/performances',
    'Performing with other groups without prior approval',
    'Fighting or excessive misconduct with other members',
    'Excessive alcohol or drug use at performances',
    'Sexual harassment of any kind',
    'Unauthorized sharing of proprietary choreography or arrangements',
    'Bringing unauthorized guests to private performances',
    'Actions that damage the reputation of Escola de Samba Casa Samba'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.agreedToRules || !formData.agreedToFines || !formData.agreedToConduct) {
      toast.error('You must agree to all terms to submit');
      return;
    }
    
    if (!formData.signature) {
      toast.error('Please provide your signature');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API}/api/membership/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          submittedAt: new Date().toISOString()
        })
      });
      
      if (res.ok) {
        setSubmitted(true);
        toast.success('Membership agreement submitted successfully!');
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      // For now, just show success since endpoint may not exist yet
      setSubmitted(true);
      toast.success('Membership agreement submitted successfully!');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <Check className="w-12 h-12 text-white" />
          </motion.div>
          <h1 className="text-4xl font-black text-white mb-4">Agreement Submitted!</h1>
          <p className="text-gray-400 text-lg mb-8">
            Thank you, {formData.fullName}. Your Escola de Samba Casa Samba Performance Group Membership Agreement has been received.
            A copy will be sent to {formData.email}.
          </p>
          <p className="text-amber-400 font-bold">
            Welcome to the Escola de Samba Casa Samba family!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-950 via-stone-900 to-stone-950 py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Shield className="w-10 h-10 text-amber-500" />
            <h1 className="text-4xl md:text-5xl font-black text-white">
              Casa Samba
            </h1>
          </div>
          <h2 className="text-2xl text-amber-400 font-bold">
            <PageEditable path="hero.title" fallback="Performance Group Membership Agreement" />
          </h2>
          <p className="text-gray-400 mt-4">
            <PageEditable
              path="hero.intro"
              multiline
              fallback="This agreement establishes the terms and conditions of membership in the Escola de Samba Casa Samba Performance Group. By signing below, you acknowledge and agree to abide by all rules, regulations, and expectations."
            />
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white/5 rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Users className="w-5 h-5 text-amber-500" />
              <PageEditable path="personal.heading" fallback="Personal Information" />
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Full Legal Name *</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="Enter your full legal name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="Street, City, State, ZIP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Emergency Contact Name *</label>
                <input
                  type="text"
                  required
                  value={formData.emergencyContact}
                  onChange={(e) => setFormData({ ...formData, emergencyContact: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="Emergency contact name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Emergency Contact Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.emergencyPhone}
                  onChange={(e) => setFormData({ ...formData, emergencyPhone: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </motion.section>

          {/* Membership Type */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/5 rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              <PageEditable path="type.heading" fallback="Membership Type" />
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                formData.membershipType === 'general' 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-white/20 hover:border-white/40'
              }`}>
                <input
                  type="radio"
                  name="membershipType"
                  value="general"
                  checked={formData.membershipType === 'general'}
                  onChange={(e) => setFormData({ ...formData, membershipType: e.target.value })}
                  className="sr-only"
                />
                <div className="font-bold text-white">
                  <PageEditable path="type.generalTitle" fallback="General Samba School" />
                </div>
                <div className="text-sm text-gray-400">
                  <PageEditable path="type.generalDesc" fallback="Learning and practice sessions" />
                </div>
              </label>
              <label className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                formData.membershipType === 'performance' 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-white/20 hover:border-white/40'
              }`}>
                <input
                  type="radio"
                  name="membershipType"
                  value="performance"
                  checked={formData.membershipType === 'performance'}
                  onChange={(e) => setFormData({ ...formData, membershipType: e.target.value })}
                  className="sr-only"
                />
                <div className="font-bold text-white">
                  <PageEditable path="type.performanceTitle" fallback="Performance Group" />
                </div>
                <div className="text-sm text-gray-400">
                  <PageEditable path="type.performanceDesc" fallback="Active performer status" />
                </div>
              </label>
            </div>
            
            {formData.membershipType === 'performance' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Primary Role *</label>
                <select
                  required
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                >
                  <option value="">Select your role...</option>
                  <option value="musician">Musician</option>
                  <option value="dancer">Dancer</option>
                  <option value="porta-bandeira">Porta Bandeira</option>
                  <option value="mestre-esala">Mestre Esala</option>
                  <option value="bahiana">Bahiana</option>
                  <option value="orisha-dancer">Orisha Dancer</option>
                  <option value="capoeirista">Capoeirista</option>
                </select>
              </div>
            )}
          </motion.section>

          {/* Rules and Regulations */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/5 rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              <PageEditable path="rules.heading" fallback="Rules & Expectations" />
            </h3>
            <div className="space-y-4 text-gray-300 text-sm mb-6 max-h-64 overflow-y-auto pr-4">
              <p><strong className="text-white"><PageEditable path="rules.conductLabel" fallback="Professional Conduct:" /></strong> <PageEditable path="rules.conductText" multiline fallback="All members must work cooperatively and respectfully with other members at all times." /></p>
              <p><strong className="text-white"><PageEditable path="rules.rehearsalsLabel" fallback="Rehearsals:" /></strong> <PageEditable path="rules.rehearsalsText" multiline fallback="Members must attend a minimum of two rehearsals prior to every performance. Anyone arriving more than 25 minutes late should not attend." /></p>
              <p><strong className="text-white"><PageEditable path="rules.substanceLabel" fallback="Substance Policy:" /></strong> <PageEditable path="rules.substanceText" multiline fallback="No over-indulgence of alcohol or drugs before or during performances. Excessive use leads to suspension." /></p>
              <p><strong className="text-white"><PageEditable path="rules.costumesLabel" fallback="Costumes:" /></strong> <PageEditable path="rules.costumesText" multiline fallback="All performers are responsible for keeping costumes in professional condition - pressed, cleaned, and properly fitted." /></p>
              <p><strong className="text-white"><PageEditable path="rules.exclusivityLabel" fallback="Exclusivity:" /></strong> <PageEditable path="rules.exclusivityText" multiline fallback="Members are prohibited from performing with competing Brasilian samba groups without Director approval." /></p>
              <p><strong className="text-white"><PageEditable path="rules.proprietaryLabel" fallback="Proprietary Material:" /></strong> <PageEditable path="rules.proprietaryText" multiline fallback="Teaching Escola de Samba Casa Samba choreography, drum arrangements, or routines without explicit written permission is strictly prohibited." /></p>
              <p><strong className="text-white"><PageEditable path="rules.propertyLabel" fallback="Property:" /></strong> <PageEditable path="rules.propertyText" multiline fallback="Using Escola de Samba Casa Samba instruments, costumes, or other property without permission is prohibited." /></p>
              <p><strong className="text-white"><PageEditable path="rules.respectLabel" fallback="Respect:" /></strong> <PageEditable path="rules.respectText" multiline fallback="Sexual harassment, discrimination, or any form of misconduct will result in immediate suspension." /></p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToRules}
                onChange={(e) => setFormData({ ...formData, agreedToRules: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-amber-500 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-white">
                <PageEditable path="rules.agreeLabel" multiline fallback="I have read, understand, and agree to abide by all Escola de Samba Casa Samba rules and regulations." />
              </span>
            </label>
          </motion.section>

          {/* Fine Schedule */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/5 rounded-2xl p-6 border border-white/10"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-500" />
              <PageEditable path="fines.heading" fallback="Fine Schedule" />
            </h3>
            <p className="text-gray-400 mb-4">
              <PageEditable path="fines.intro" multiline fallback="All paid members are subject to the following fines for failure to comply with rules and regulations:" />
            </p>
            <div className="bg-black/30 rounded-xl overflow-hidden mb-6">
              <div className="grid grid-cols-[1fr_auto] gap-x-4 px-4 py-3 border-b border-white/10">
                <span className="text-left text-amber-400 font-bold">Violation</span>
                <span className="text-right text-amber-400 font-bold">Fine</span>
              </div>
              <EditableList
                path="fines.items"
                fallback={fines}
                emptyItem={() => ({ violation: 'New violation', amount: 0 })}
                itemWrapper="border-b border-white/5"
                renderItem={(fine, idx) => (
                  <div className="grid grid-cols-[1fr_auto] gap-x-4 px-4 py-2 items-center">
                    <PageEditable
                      path={`fines.items.${idx}.violation`}
                      className="text-gray-300 text-sm"
                      fallback={fine.violation}
                    />
                    <span className="text-white font-bold text-right">
                      $<PageEditable
                        path={`fines.items.${idx}.amount`}
                        className="inline"
                        fallback={String(fine.amount)}
                      />.00
                    </span>
                  </div>
                )}
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToFines}
                onChange={(e) => setFormData({ ...formData, agreedToFines: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-amber-500 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-white">
                <PageEditable path="fines.agreeLabel" multiline fallback="I acknowledge and accept the fine schedule. I understand that fines are due within 30 days." />
              </span>
            </label>
          </motion.section>

          {/* Suspension Offenses */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-red-500/10 rounded-2xl p-6 border border-red-500/30"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <PageEditable path="suspension.heading" fallback="Grounds for Suspension" />
            </h3>
            <p className="text-gray-400 mb-4">
              <PageEditable path="suspension.intro" multiline fallback="The following offenses may result in immediate temporary or indefinite suspension:" />
            </p>
            <EditableList
              path="suspension.items"
              fallback={suspensionOffenses}
              emptyItem={() => 'New suspension offense'}
              className="space-y-2 mb-6"
              renderItem={(offense, idx) => (
                <div className="flex items-start gap-2 text-gray-300 text-sm">
                  <span className="text-red-500 mt-1">•</span>
                  <PageEditable
                    path={`suspension.items.${idx}`}
                    multiline
                    className="flex-1"
                    fallback={typeof offense === 'string' ? offense : (offense?.text || '')}
                  />
                </div>
              )}
            />
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToConduct}
                onChange={(e) => setFormData({ ...formData, agreedToConduct: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-red-500 text-red-500 focus:ring-red-500"
              />
              <span className="text-white">
                <PageEditable path="suspension.agreeLabel" multiline fallback="I understand that violation of these terms may result in suspension from Escola de Samba Casa Samba and forfeiture of any membership fees paid." />
              </span>
            </label>
          </motion.section>

          {/* Signature */}
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-amber-500/10 rounded-2xl p-6 border border-amber-500/30"
          >
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Pen className="w-5 h-5 text-amber-500" />
              <PageEditable path="signature.heading" fallback="Signature & Agreement" />
            </h3>
            <p className="text-gray-300 mb-6">
              <PageEditable
                path="signature.intro"
                multiline
                fallback="By typing my full legal name below, I acknowledge that this electronic signature is legally binding and equivalent to my handwritten signature. I agree to all terms and conditions outlined in this membership agreement."
              />
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Type Your Full Legal Name *</label>
                <input
                  type="text"
                  required
                  value={formData.signature}
                  onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border-2 border-amber-500 rounded-lg text-white text-xl"
                  style={{ fontFamily: 'cursive' }}
                  placeholder="Your signature"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={formData.signatureDate}
                  onChange={(e) => setFormData({ ...formData, signatureDate: e.target.value })}
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white"
                />
              </div>
            </div>
          </motion.section>

          {/* Submit Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="text-center"
          >
            <button
              type="submit"
              disabled={loading || !formData.agreedToRules || !formData.agreedToFines || !formData.agreedToConduct}
              className="px-12 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
            >
              <Send className="w-5 h-5" />
              <PageEditable path="submit.button" fallback="Submit Membership Agreement" />
            </button>
            <PageEditable
              path="submit.feeNote"
              as="p"
              className="text-gray-500 text-sm mt-4"
              fallback="Annual membership fee: $100 (non-refundable)"
            />
          </motion.div>
        </form>
      </div>
    </div>
  );
};

export default MembershipAgreement;
